import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ModerateReview,
  ReplyToReview,
  RequestReview,
  Review,
  ReviewSettings,
  ReviewStatus,
  UpsertReviewSettings,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS13 Feature 1 — REVIEWS (LRU-05). TanStack Query over the generated client, mirroring data/stock.ts
 * (unwrap/expectOk, body `as never`, onSuccess invalidate).
 *
 * HONESTY (non-negotiable): a review is a salon-PUBLIC artifact — it renders on the public booking page
 * and feeds the JSON-LD aggregateRating. The list response's `averageRating` / `reviewCount` are SERVER
 * figures computed over the PUBLISHED rows; the console DISPLAYS them and never recomputes an aggregate
 * in JS. A moderation action (publish/hide) changes which rows are counted, so after ANY write —
 * moderate, reply, or a settings change that flips new reviews' starting status — we invalidate the
 * whole `['reviews']` tree so every list AND the aggregate re-read from the server together.
 *
 * The author name is the server's DENORMALISED `authorDisplayName` (e.g. "Sarah M."): a sealed/hidden
 * client's real name never crosses the wire. The console reads `review.staffId` for the per-stylist
 * breakdown but renders only display strings; it never joins back to the clients table.
 *
 * RBAC: the LIST read is allowed for any staff role (RLS-scoped). Moderation, reply, settings and
 * request-review are manager+ on the API; the page self-gates non-managers to an honest note rather than
 * a guaranteed 403, so these mutations are only ever invoked from a manager session.
 */

export interface ReviewListResult {
  data: Review[];
  averageRating: number;
  reviewCount: number;
  pagination: { limit: number; offset: number; total: number };
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * The operator moderation list (+ the salon aggregate that drives the header), optionally filtered by
 * status. The list endpoint returns 200 with an empty page when there is no data, so there is no 404 to
 * map; a genuine error surfaces via `isError`, which the panel renders honestly. Reads live (staleTime
 * 0): the aggregate is a public-facing figure that must reflect the latest moderation state.
 */
export function useReviews(status: ReviewStatus | null) {
  return useQuery({
    queryKey: qk.reviews(status),
    staleTime: 0,
    queryFn: async (): Promise<ReviewListResult> =>
      unwrap(
        await api.GET('/v1/reviews', {
          params: { query: status ? { status } : {} },
        }),
      ),
  });
}

/** The per-salon review settings (moderation mode + auto-request opt-in/delay). Manager-only surface. */
export function useReviewSettings() {
  return useQuery({
    queryKey: qk.reviewSettings,
    queryFn: async (): Promise<ReviewSettings> => unwrap(await api.GET('/v1/reviews/settings')),
  });
}

// ── Mutations (manager + on the API) ────────────────────────────────────────────────

/**
 * Moderate a review: publish or hide it (optionally attaching/replacing the salon reply in the same
 * call). Either status flip changes which rows the public aggregate counts, so invalidate the whole
 * reviews tree — every status tab and the header aggregate re-read.
 */
export function useModerateReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reviewId: string; body: ModerateReview }): Promise<Review> =>
      unwrap(
        await api.PATCH('/v1/reviews/{reviewId}', {
          params: { path: { reviewId: input.reviewId } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => invalidateReviews(qc),
  });
}

/** Attach (or replace) the salon's public reply to a review. The reply renders publicly beneath it. */
export function useReplyToReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reviewId: string; body: ReplyToReview }): Promise<Review> =>
      unwrap(
        await api.POST('/v1/reviews/{reviewId}/reply', {
          params: { path: { reviewId: input.reviewId } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => invalidateReviews(qc),
  });
}

/**
 * Mint a review-request token for a COMPLETED appointment and enqueue the gated send. Idempotent on
 * (salon, appointment): a re-request returns the existing token (the server reports `alreadyRequested`),
 * so a double-tap is a safe no-op. No review row exists yet, so no list invalidation is needed.
 */
export function useRequestReview() {
  return useMutation({
    mutationFn: async (body: RequestReview): Promise<{ ok: true; alreadyRequested: boolean }> =>
      unwrap(await api.POST('/v1/reviews/request', { body: body as never })),
  });
}

/**
 * Upsert the per-salon review settings (moderation mode + auto-request toggle/delay — all fields
 * optional, partial update). Flipping moderation to 'manual' changes the starting status of FUTURE
 * reviews (it does not retro-moderate existing rows), but we invalidate the whole reviews tree anyway so
 * the settings cache and any open list stay consistent.
 */
export function useUpdateReviewSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpsertReviewSettings): Promise<ReviewSettings> =>
      unwrap(await api.PUT('/v1/reviews/settings', { body: body as never })),
    onSuccess: (settings) => {
      qc.setQueryData(qk.reviewSettings, settings);
      invalidateReviews(qc);
    },
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────

/**
 * Any moderate/reply/settings write can shift which rows the public aggregate counts, so every reviews
 * list (each status tab) AND the settings cache re-read together. One prefix covers both — the keys are
 * `['reviews', { status }]` and `['reviews', 'settings']`.
 */
function invalidateReviews(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['reviews'] });
}
